"use client";

import { motion, useDragControls } from "motion/react";
import { forwardRef, memo, useCallback, useMemo } from "react";

import { cn } from "../../utils/cn";

import type { HTMLMotionProps, PanInfo } from "motion/react";
import type { StackScreenRendererProps } from "./types";

// ─────────────────────────────────────────────────────────────────────────────
// Animation Configuration
// ─────────────────────────────────────────────────────────────────────────────

const DURATION = 0.25;
const EASE = [0.25, 0.1, 0.25, 1.0] as const;
const transition = { type: "tween" as const, duration: DURATION, ease: EASE };

// Push transition: horizontal slide (iOS-style navigation)
const INITIAL_PUSH = { x: "100%", y: 0 };
const EXIT_PUSH = { x: "100%", y: 0, transition };

// Replace transition: vertical slide (in-place content replacement)
const INITIAL_REPLACE = { x: 0, y: "100%" };
const EXIT_REPLACE = { x: 0, y: "100%", transition };

// ─────────────────────────────────────────────────────────────────────────────
// Swipe-to-go-back Configuration
// ─────────────────────────────────────────────────────────────────────────────

const SWIPE_THRESHOLD = 100;
const DRAG_CONSTRAINTS = { left: 0, right: 0 };

/**
 * Memoized screen renderer for optimal performance.
 * Only re-renders when its specific props change.
 * Uses forwardRef to support AnimatePresence popLayout mode.
 * Supports swipe-from-edge gesture to go back on touch devices.
 */
export const StackScreenRenderer = memo(
  forwardRef<HTMLDivElement, StackScreenRendererProps>(function StackScreenRenderer(
    { item, index, isTop, canPop, onPop, screens, screenClassName, topIsReplace },
    ref
  ) {
    const Component = screens[item.name];
    const isReplace = item.transitionType === "replace";
    const dragControls = useDragControls();

    const handlePointerDown = useCallback(
      (e: React.PointerEvent) => {
        if (e.pointerType === "touch") {
          dragControls.start(e);
        }
      },
      [dragControls]
    );

    const handleDragEnd = useCallback(
      (_: unknown, info: PanInfo) => {
        if (canPop && info.offset.x > SWIPE_THRESHOLD && info.velocity.x > 0) {
          onPop();
        }
      },
      [canPop, onPop]
    );

    const className = cn(
      "absolute inset-0 size-full overflow-y-auto overflow-x-hidden bg-white dark:bg-gray-900",
      !isTop && "pointer-events-none overflow-hidden",
      screenClassName
    );

    const style = useMemo(
      () => ({
        zIndex: index,
        pointerEvents: isTop ? ("auto" as const) : ("none" as const),
      }),
      [index, isTop]
    );

    const animate = useMemo(
      () => ({ x: isTop || topIsReplace ? 0 : "-30%", y: 0, transition }),
      [isTop, topIsReplace]
    );

    // Skip animation for initial screen (index 0 with no transitionType means it came from reset/mount)
    // Replace operations at index 0 should still animate
    const isInitialMount = index === 0 && !item.transitionType;
    const initial = isInitialMount ? false : isReplace ? INITIAL_REPLACE : INITIAL_PUSH;
    const exit = isReplace ? EXIT_REPLACE : EXIT_PUSH;

    if (!Component) return null;

    return (
      <motion.div
        ref={ref}
        initial={initial}
        animate={animate}
        exit={exit}
        drag={isTop && canPop ? "x" : false}
        dragControls={dragControls}
        dragListener={false}
        dragConstraints={DRAG_CONSTRAINTS}
        dragElastic={0.2}
        dragSnapToOrigin
        onDragEnd={handleDragEnd}
        {...({
          onPointerDown: isTop && canPop ? handlePointerDown : undefined,
          className,
          style,
        } as HTMLMotionProps<"div">)}
      >
        <Component {...item.params} />
      </motion.div>
    );
  })
);

StackScreenRenderer.displayName = "StackScreenRenderer";
